import { CuePopup } from "common/Map/CuePopup";
import { CueTooltip } from "common/Map/CueTooltip";
import { Marker } from "common/Map/Marker";
import { Popup } from "common/Map/Popup";
import { featureTypes } from "common/MapV2/featureTypes";
import { TrackLine } from "common/MapV2/TrackLine";
import { SegmentPopup } from "common/SegmentPopup";
import { TrackLocation } from "data/records";
import { commonNouns } from "lib/localization";
import { softError } from "lib/softError";
import _ from "lodash";
import React from "react";

// Render the selected feature to the map. Only Map primitives go in here!
export function renderSelectedFeature(showPopover = true) {
  const { selectedFeature, mapZoomed, mapSettings } = this.state;
  const showTrackSurface = mapSettings?.showTrackSurface;
  const { troute, intl } = this.props;
  if (!selectedFeature) {
    return null;
  }
  if (!featureTypes[selectedFeature.type]) {
    softError(`unknown selected feature type ${selectedFeature.type}`);
    return null;
  }
  if (!selectedFeature.data) {
    softError(`invalid selectedFeature ${selectedFeature.type} (missing a data object)`);
    return null;
  }

  const onZoomClicked = () =>
    mapZoomed ? this.zoomToTRouteBounds(false) : this.zoomToSliceBounds(selectedFeature.data.highlightSlice, false);

  const popover = (() => {
    switch (selectedFeature.type) {
      case featureTypes.SAMPLE_GRAPH_USER_SELECTION:
        return (
          <SegmentPopup
            route={troute}
            highlightSlice={selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
            mapZoomed={mapZoomed}
            onZoomClicked={onZoomClicked}
            onRequestClose={this.clearSelectedFeature}
          />
        );
      case featureTypes.HILL:
        return (
          <SegmentPopup
            route={troute}
            labelPrefix={_.capitalize(
              intl.formatMessage(selectedFeature.data.hill.delta_e > 0 ? commonNouns.climbs : commonNouns.descents, {
                count: 1,
              })
            )}
            highlightSlice={selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
            mapZoomed={mapZoomed}
            onZoomClicked={onZoomClicked}
            onRequestClose={this.clearSelectedFeature}
          />
        );
      case featureTypes.LAP:
        return (
          <SegmentPopup
            route={troute}
            labelPrefix={`${_.capitalize(intl.formatMessage(commonNouns.laps, { count: 1 }))} #${
              selectedFeature.data.lapNumber
            }`}
            highlightSlice={selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
            mapZoomed={mapZoomed}
            onZoomClicked={onZoomClicked}
            onRequestClose={this.clearSelectedFeature}
          />
        );
      case featureTypes.SEGMENT_MATCH:
        return (
          <SegmentPopup
            label={selectedFeature.data.segmentMatch.segmentTitle}
            route={troute}
            highlightSlice={selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
            mapZoomed={mapZoomed}
            onZoomClicked={onZoomClicked}
            onRequestClose={this.clearSelectedFeature}
          />
        );
      case featureTypes.CUE:
        return (
          <CuePopup autoPan coursePoint={selectedFeature.data.coursePoint} onRequestClose={this.clearSelectedFeature} />
        );
      default:
        return null;
    }
  })();

  const highlightSlice = selectedFeature.data.highlightSlice && (
    <TrackLine
      color="#0a68bb"
      showStartMarker={false}
      showEndMarker={false}
      showSurface={showTrackSurface}
      trackPoints={troute.trackPoints}
      slice={selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
      objectId={troute.id + "selection"}
    />
  );

  return (
    <>
      {highlightSlice}
      {showPopover && popover}
    </>
  );
}

export function renderHoveredFeature() {
  const { hoveredFeature, selectedFeature } = this.state;
  const { troute } = this.props;
  if (!hoveredFeature) {
    return null;
  }
  if (!featureTypes[hoveredFeature.type]) {
    softError(`unknown selected feature type ${hoveredFeature.type}`);
    return null;
  }
  if (!hoveredFeature.data) {
    softError(`invalid hoveredFeature ${hoveredFeature.type} (missing a data object)`);
    return null;
  }

  const popover = (() => {
    switch (hoveredFeature.type) {
      case featureTypes.CUE:
        return (
          _.get(selectedFeature, "type") !== featureTypes.CUE && (
            <CueTooltip coursePoint={hoveredFeature.data.coursePoint} />
          )
        );
      case featureTypes.POINT_METRIC:
        return (
          <Popup
            closeButton={false}
            position={{
              lat: hoveredFeature.data.highlightPoint.trackPoint.lat,
              lng: hoveredFeature.data.highlightPoint.trackPoint.lng,
            }}
            maxWidth={200}
            minWidth={50}
          >
            {hoveredFeature.data.label}
          </Popup>
        );
      default:
        return null;
    }
  })();

  const highlightSlice = hoveredFeature.data.highlightSlice && (
    <>
      <TrackLine
        color="#FFF"
        weight={7}
        showStartMarker={false}
        showEndMarker={false}
        trackPoints={troute.trackPoints}
        slice={hoveredFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
      />
      <TrackLine
        color="#0a68bb"
        showStartMarker={false}
        showEndMarker={false}
        trackPoints={troute.trackPoints}
        slice={hoveredFeature.data.highlightSlice.map(TrackLocation.closestIndex)}
      />
    </>
  );

  const marker = hoveredFeature.data.highlightPoint && (
    <Marker position={hoveredFeature.data.highlightPoint.trackPoint} icon="highlightPoint" />
  );

  return (
    <>
      {popover}
      {highlightSlice}
      {marker}
    </>
  );
}
